import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  Phone,
  MapPin, 
  Clock,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  Badge
} from '../styles/GlobalStyles';

const ContactSection = styled(Section)`
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--primary-50) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  .badge {
    margin-bottom: var(--space-4);
  }
  
  h1 {
    font-size: var(--text-5xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ContactGrid = styled(Grid)`
  gap: var(--space-12);
  
  @media (max-width: 768px) {
    gap: var(--space-8);
  }
`;

const ContactInfo = styled.div`
  background: var(--gray-0);
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--gray-200);
  
  h2 {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
  }
  
  .description {
    color: var(--gray-600);
    margin-bottom: var(--space-8);
    line-height: 1.6;
  }
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
  cursor: pointer;
  
  &:hover {
    background: var(--gray-50);
    transform: translateX(4px);
  }
  
  .icon {
    width: 48px;
    height: 48px;
    background: var(--gradient-primary);
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-0);
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    
    .label {
      font-weight: 600;
      color: var(--gray-900);
      margin-bottom: var(--space-1);
    }
    
    .value {
      color: var(--gray-600);
      font-size: var(--text-sm);
    }
  }
`;

const QuickContact = styled(Card)`
  background: var(--gray-0);
  border: 1px solid var(--gray-200);
  
  h2 {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
  }
  
  .description {
    color: var(--gray-600);
    margin-bottom: var(--space-6);
    line-height: 1.6;
  }
`;

const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  background: ${props => props.primary ? 'var(--gradient-primary)' : 'var(--gray-100)'};
  color: ${props => props.primary ? 'var(--gray-0)' : 'var(--gray-700)'};
  border: none;
  border-radius: var(--radius-lg);
  font-weight: 600;
  font-size: var(--text-base);
  transition: all var(--transition-fast);
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    background: ${props => props.primary ? 'linear-gradient(135deg, var(--primary-600) 0%, var(--blue-600) 100%)' : 'var(--gray-200)'};
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--green-100);
  color: var(--green-700);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: 500;
  margin-top: var(--space-4);
`;

const Contact = () => {
  const [emailCopied, setEmailCopied] = useState(false);

  const handleEmailClick = () => {
    const email = 'admin@willovate.in';
    const subject = 'Project Inquiry';
    const body = 'Hello Willovate team,\n\nI would like to discuss a potential project with you.\n\nBest regards,';
    
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('admin@willovate.in');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const contactMethods = [
    {
      icon: <Mail size={20} />,
      label: 'Email Us',
      value: 'admin@willovate.in',
      action: handleEmailClick
    },
    {
      icon: <Phone size={20} />,
      label: 'Call Us',
      value: '+91 9356603646',
      action: () => window.location.href = 'tel:+919356603646'
    },
    {
      icon: <ExternalLink size={20} />,
      label: 'View Our Work',
      value: 'WillovateResto.com',
      action: () => window.open('https://willovateresto.com', '_blank')
    },
    {
      icon: <MapPin size={20} />,
      label: 'Location',
      value: 'Remote Team, India',
      action: null
    }
  ];

  return (
    <ContactSection>
      <Container>
        <SectionHeader>
          <Badge className="badge primary">
            <Mail size={12} />
            Get In Touch
          </Badge>
          <h1>Let's Build Something Great Together</h1>
          <p className="subtitle">
            Ready to turn your idea into reality? We'd love to hear about your project 
            and discuss how we can help bring it to life.
          </p>
        </SectionHeader>

        <ContactGrid className="cols-2">
          <ContactInfo>
            <h2>Contact Information</h2>
            <p className="description">
              We're a growing software development team passionate about creating 
              solutions that make a difference. Reach out to discuss your project needs.
            </p>
            
            {contactMethods.map((method, index) => (
              <ContactMethod 
                key={index} 
                onClick={method.action}
                style={{ cursor: method.action ? 'pointer' : 'default' }}
              >
                <div className="icon">
                  {method.icon}
                </div>
                <div className="content">
                  <div className="label">{method.label}</div>
                  <div className="value">{method.value}</div>
                </div>
              </ContactMethod>
            ))}
            
            <StatusBadge>
              <CheckCircle size={14} />
              Currently accepting new projects
            </StatusBadge>
          </ContactInfo>

          <QuickContact>
            <h2>Quick Contact</h2>
            <p className="description">
              The fastest way to reach us is through email. We typically respond within 24 hours 
              and would love to learn more about your project requirements.
            </p>
            
            <QuickActions>
              <ActionButton primary onClick={handleEmailClick}>
                <Mail size={18} />
                Send Email
              </ActionButton>
              
              <ActionButton onClick={() => window.open('https://willovateresto.com', '_blank')}>
                <ExternalLink size={18} />
                View Live Project
              </ActionButton>
              
              <ActionButton onClick={handleCopyEmail}>
                {emailCopied ? <CheckCircle size={18} /> : <Mail size={18} />}
                {emailCopied ? 'Email Copied!' : 'Copy Email Address'}
              </ActionButton>
            </QuickActions>
            
            <div style={{ marginTop: 'var(--space-6)', padding: 'var(--space-4)', background: 'var(--gray-50)', borderRadius: 'var(--radius-lg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                <Clock size={16} />
                <strong>Response Time</strong>
              </div>
              <p style={{ color: 'var(--gray-600)', fontSize: 'var(--text-sm)', margin: 0 }}>
                We aim to respond to all inquiries within 24 hours during business days.
              </p>
            </div>
          </QuickContact>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact;
